import DataTable from '@/util/DataTable';
import ColumnName from '@/util/ColumnName';
import DataTableHeading from '@/util/DataTableHeading';
import DataTableRow from '@/util/DataTableRow';
import DataTableCell from '@/util/DataTableCell';
import { TChartType } from '@/features/chart-configuration/apiSlice';
import { FLOAT, INT } from '@/util/ColumnType';
import {
  BAR,
  BARLINE,
  LINE,
  PIE,
  PIVOT,
  STACKED_CHART,
} from '@/components/charting/chartType.constants';
import ChartHolder from '@/components/ChartHolder';

export const CONFIGURABLE_CHART_TYPES = [
  PIVOT,
  BAR,
  LINE,
  PIE,
  BARLINE,
  STACKED_CHART,
  'single_value',
];

export function getDataTableForMetricsAndDimensions(
  dataTable: DataTable,
  columns: Nullable<ColumnName[]>,
): DataTable {
  if (!columns || columns.length === 0) return dataTable;

  const headings: DataTableHeading[] = dataTable.headings();
  const rows: DataTableRow[] = dataTable.rows();

  const indexes = findIndexes(columns, headings);

  const newHeadings: DataTableHeading[] = [];
  const newRows: DataTableRow[] = [];

  indexes.forEach((idx) => newHeadings.push(headings[idx]));

  rows.forEach((row) => {
    const cells: DataTableCell[] = [];

    indexes.forEach((idx) => cells.push(row.cells()[idx]));
    newRows.push(new DataTableRow(row.rowId(), cells));
  });

  return new DataTable(
    newHeadings,
    newRows,
    dataTable.aliases(),
    dataTable.drilldownOptions(),
    dataTable.readOnly(),
  );
}

export function getDataTableHeadingsForMetricsAndDimensions(
  dataTable: DataTable,
  columns: Nullable<ColumnName[]>,
): DataTableHeading[] {
  if (!columns || columns.length === 0) return dataTable.headings();

  const headings: DataTableHeading[] = dataTable.headings();
  const indexes = findIndexes(columns, headings);

  return indexes.map((idx) => headings[idx]) as DataTableHeading[];
}

function findIndexes(
  columns: ColumnName[],
  headings: DataTableHeading[],
): number[] {
  const indexes: number[] = [];
  columns.forEach((column) => {
    headings.forEach((heading: DataTableHeading, i: number) => {
      const h = heading.columnName();
      if (column._name === h._name && column._alias === h._alias) {
        indexes.push(i);
      }
    });
  });

  return indexes;
}

export function getMetricsAndDimensionsForLegacyWidgets(
  dataTable: Maybe<DataTable>,
  chartType: TChartType,
) {
  if (!dataTable) return EMPTY_CONFIG;
  switch (chartType) {
    case 'table':
      return EMPTY_CONFIG;
    case 'single_value':
      if (dataTable.headings().length === 1) {
        return {
          metrics: [getColumnName(dataTable, 0)],
        };
      }
      if (dataTable.headings().length === 2) {
        return {
          dimensions: [getColumnName(dataTable, 0)],
          metrics: [getColumnName(dataTable, 1)],
        };
      }
      return EMPTY_CONFIG;
    case 'bar':
    case 'line':
      if (dataTable.headings().length < 2) {
        return EMPTY_CONFIG;
      }
      if (dataTable.headings().length === 3) {
        if (
          dataTable.headings()[1].columnType() !== INT ||
          dataTable.headings()[1].columnType() !== FLOAT
        ) {
          return {
            dimensions: [
              getColumnName(dataTable, 0),
              getColumnName(dataTable, 1),
            ],
            metrics: [getColumnName(dataTable, 2)],
          };
        }
      }
      return {
        dimensions: [getColumnName(dataTable, 0)],
        metrics: dataTable
          .headings()
          .slice(1)
          .map((h: DataTableHeading) => h.columnName()),
      };
    case 'pie':
      if (dataTable.headings().length === 2) {
        return {
          dimensions: [getColumnName(dataTable, 0)],
          metrics: [getColumnName(dataTable, 1)],
        };
      }
      if (dataTable.headings().length === 3) {
        return {
          dimensions: [
            getColumnName(dataTable, 0),
            getColumnName(dataTable, 1),
          ],
          metrics: [getColumnName(dataTable, 2)],
        };
      }
      return EMPTY_CONFIG;
    case 'pivot':
      if (dataTable.headings().length < 3) {
        return EMPTY_CONFIG;
      }
      return {
        dimensions: dataTable
          .headings()
          .slice(0, dataTable.headings().length - 1)
          .map((h: DataTableHeading) => h.columnName()),
        metrics: [getColumnName(dataTable, dataTable.headings().length - 1)],
      };
    case 'bar_line':
      if (dataTable.headings().length !== 3) {
        return EMPTY_CONFIG;
      }
      return {
        dimensions: [getColumnName(dataTable, 0)],
        metrics: [getColumnName(dataTable, 1), getColumnName(dataTable, 2)],
      };
    case 'stacked':
      if (dataTable.headings().length === 3) {
        return {
          dimensions: [
            getColumnName(dataTable, 0),
            getColumnName(dataTable, 1),
          ],
          metrics: [getColumnName(dataTable, 2)],
        };
      }
      return EMPTY_CONFIG;
  }
}

export function validateDataTable(
  headings: DataTableHeading[],
  chartType: TChartType,
): boolean {
  if (!CONFIGURABLE_CHART_TYPES.includes(chartType)) {
    return true;
  }

  // @ts-expect-error: TODO: IDK why, but it thinks that the second parameter should be null | undefined.
  return ChartHolder.canHandleHeadings(headings, chartType);
}

const EMPTY_CONFIG = {
  dimensions: [],
  metrics: [],
};

function getColumnName(dataTable: DataTable, index: number) {
  return dataTable.headings()[index].columnName();
}
